import { Component, effect, inject } from '@angular/core';
import { Router, RouterLink } from '@angular/router';

import { IconComponent } from '@reforgium/icon';
import { LangPipe, LangService, RouteWatcher, SeoService } from '@reforgium/presentia';
import { SkButtonComponent } from '@ss/ui';

import { urls } from 'constants/urls.constant';

import { BackToTopComponent } from 'components/back-to-top';

import { DocumentLangs } from 'entities/document.model';
import { AuthService } from 'use-cases/auth.service';
import { DocumentService } from 'use-cases/document.service';
import { FilterPanel } from 'features/panels/filter-panel';

import { ArticleCardComponent } from './components';

@Component({
  selector: 'app-documents-list-page',
  template: `
    <h1 class="sr-only">{{ 'document.titles.list-documents' | lang }}</h1>
    <div class="documents-container">
      <section>
        <div class="flex items-center justify-between">
          <a class="back-link" routerLink="/"
            ><re-icon name="arrow-right" class="rotate-180" width="16" height="16" />{{ 'common.back' | lang }}</a
          >
          @if (authService.isAuthenticated) {
            <sk-button
              icon="plus"
              [label]="'administrative.buttons.addDocument' | lang"
              (clicked)="handleDocumentAdd()"
            />
          }
        </div>

        <div class="grid gap-2 mt-3 w-full">
          @if (documentService.documents.loading()) {
            @for (_ of [].constructor(4); track $index) {
              <app-article-card isSkeleton />
            }
          } @else if (!documentService.documents.items().length) {
            <div class="text-center mt-20">{{ 'document.no-data' | lang }}</div>
          } @else {
            @for (
              document of documentService.documents.cached();
              track langService.currentLang() + document.lastVersionId
            ) {
              <app-article-card
                [versionId]="document.lastVersionId"
                [documentId]="document.documentId"
                [title]="document.title"
                [status]="document.status"
                [description]="document.createDate"
                [versionCount]="document.versionCount"
                [categoryId]="categoryId()"
              />
            }
          }
        </div>
        <app-back-to-top />
      </section>

      <app-filter-panel showSorting [isDocumentsLoading]="documentService.documents.loading()" />
    </div>
  `,
  imports: [
    RouterLink,
    IconComponent,
    LangPipe,
    SkButtonComponent,
    ArticleCardComponent,
    FilterPanel,
    BackToTopComponent,
  ],
  styles: `
    .back-link {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      padding: 4px 14px;
      background: var(--surface-neutral);
      color: #2da5fa;
      border: 1px solid var(--border-color);
      border-radius: 8px;
      font-size: var(--app-button-font-size);
    }
    .documents-container {
      display: grid;
      align-items: start;
      grid-template-columns: minmax(0, 1fr) minmax(0, 386px);
      gap: 20px;
    }

    app-filter-panel {
      position: sticky;
      top: 0;
      width: 100%;
      max-height: 100%;
    }

    @media (max-width: 800px) {
      .documents-container {
        grid-template-columns: 1fr;
      }

      .documents-container > app-filter-panel {
        position: static;
        order: -1;
      }
    }
  `,
  providers: [SeoService, DocumentService],
})
export class DocumentsListPage {
  private watcher = inject(RouteWatcher);
  protected readonly categoryId = this.watcher.selectQuery<string>('categoryId');
  private router = inject(Router);

  protected seoService = inject(SeoService);
  protected langService = inject(LangService);
  protected documentService = inject(DocumentService);
  protected authService = inject(AuthService);

  constructor() {
    this.seoService.setTitle('Список документов');
    this.seoService.setTwitter({
      title: 'Список документов',
    });

    effect(() => {
      const lang = this.langService.currentLang();
      const { sortBy, ...queryFilters } = this.watcher.query();
      const filters = { lang: lang.toUpperCase() as DocumentLangs, ...queryFilters };

      void this.documentService.documents.fetch({
        filters,
        sort: [{ sort: sortBy === 'createdAt' ? 'createdAt' : 'updatedAt', order: 'desc' }],
      });
    });
  }

  handleDocumentAdd() {
    void this.router.navigate([urls.documentCreation]);
  }
}
